"use client";

import {
  Popover,
  PopoverTrigger,
  PopoverPortal,
  PopoverPositioner,
  PopoverPopup,
} from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useRef, type ReactNode } from "react";
import type {
  CoworldSwitcherState,
  CoworldSwitcherLinkProps,
} from "./coworld-switcher-types";
import { CoworldSwitcherTrigger } from "./coworld-switcher-trigger";
import { CoworldSwitcherList } from "./coworld-switcher-list";

export type CoworldSwitcherViewProps = {
  readonly label: string;
  readonly compactTrigger?: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly state: CoworldSwitcherState;
  readonly onRetry: () => void;
  readonly browseHref: string;
  readonly renderLink?: (props: CoworldSwitcherLinkProps) => ReactNode;
};

export function CoworldSwitcherView({
  label,
  compactTrigger = false,
  open,
  onOpenChange,
  query,
  onQueryChange,
  state,
  onRetry,
  browseHref,
  renderLink = (props) => (
    <a {...props} href={props.href}>
      {props.children}
    </a>
  ),
}: CoworldSwitcherViewProps) {
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger>
        <CoworldSwitcherTrigger
          label={label}
          compactTrigger={compactTrigger}
          open={open}
        />
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverPositioner
          side="bottom"
          align="start"
          sideOffset={10}
          mobileSheet
        >
          <PopoverPopup
            initialFocus={searchRef}
            aria-label="Switch coworld"
            mobileSheet
          >
            <fieldset
              aria-label="Switch coworld"
              {...stylex.props(styles.group)}
            >
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="Search coworlds"
                aria-label="Search coworlds"
                {...stylex.props(styles.input)}
                onKeyDown={(event) => {
                  const first =
                    listRef.current?.querySelector<HTMLAnchorElement>(
                      "[data-switcher-row]",
                    );

                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    first?.focus();
                  } else if (event.key === "Enter") {
                    event.preventDefault();
                    first?.click();
                  }
                }}
              />
              <CoworldSwitcherList
                listRef={listRef}
                state={state}
                query={query}
                onRetry={onRetry}
                renderLink={renderLink}
                onOpenChange={onOpenChange}
                browseHref={browseHref}
              />
            </fieldset>
          </PopoverPopup>
        </PopoverPositioner>
      </PopoverPortal>
    </Popover>
  );
}

const styles = stylex.create({
  group: { borderWidth: 0, margin: 0, padding: 0, minWidth: 0 },
  input: {
    boxSizing: "border-box",
    width: "100%",
    marginBottom: spacing.space8,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderColor: { default: colors.border, ":focus": colors.foreground },
    backgroundColor: "transparent",
    paddingInline: spacing.space4,
    paddingBlock: spacing.space6,
    fontFamily: fonts.mono,
    fontSize: fontSizes.label,
    lineHeight: "18px",
    letterSpacing: "0.14px",
    color: colors.foreground,
    outline: "none",
    "::placeholder": { color: colors.secondary },
  },
});
